'use client';

import { type ReactNode, useId, useState } from 'react';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { ZoomDialog } from '../ZoomDialog/ZoomDialog.client';
import { ErrorFallback } from '../ZoomDialog/ErrorFallback/ErrorFallback.client';
import { GRID_CLASS_NAME, SLIDER_CLASS_NAME } from './tileFrame';
import { type ImageGalleryGridMedia } from './types';

/** With fewer images there is nothing to scroll. */
const MIN_IMAGES_FOR_SLIDER = 3;

export type ImageGalleryGridListProps = {
  children: ReactNode;
  media: ImageGalleryGridMedia;
  orderNumber: string;
  hasOverflow: boolean;
  showAllLabel: string;
  showLessLabel: string;
};

export const ImageGalleryGridList = ({
  children,
  media,
  orderNumber,
  hasOverflow,
  showAllLabel,
  showLessLabel,
}: ImageGalleryGridListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const gridId = useId();
  const withSlider = media.length >= MIN_IMAGES_FOR_SLIDER;

  // One handler on the list, so the tiles stay free of client code.
  const openZoom = (event: React.MouseEvent<HTMLUListElement>) => {
    const trigger = (event.target as HTMLElement).closest<HTMLElement>('[data-index]');
    if (trigger?.dataset.index) {
      setZoomIndex(Number(trigger.dataset.index));
    }
  };

  return (
    <>
      {/* The progress bar reaches the scroll timeline of its sibling list through this scope. */}
      <div className="@container/imggrid [timeline-scope:--imggrid-scroll]">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- the clicks come from the tile buttons, which handle the keyboard themselves */}
        <ul
          id={gridId}
          data-expanded={isExpanded}
          onClick={openZoom}
          className={cn(GRID_CLASS_NAME, withSlider && SLIDER_CLASS_NAME)}
        >
          {children}
        </ul>

        {withSlider && (
          // Hidden without scroll-driven animations, so it cannot freeze half full.
          <div className="bg-color-linear-progress-background supports-[animation-timeline:scroll()]:imggrid-slider:block mt-1 hidden h-0.5 w-full overflow-hidden rounded-full">
            <div className="imggrid-progress-bar bg-color-primary h-full w-full" />
          </div>
        )}
      </div>

      {hasOverflow && (
        <Button
          color="default"
          className="mt-2 w-full pointer-coarse:hidden"
          aria-expanded={isExpanded}
          aria-controls={gridId}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {isExpanded ? showLessLabel : showAllLabel}
        </Button>
      )}

      {zoomIndex !== null && (
        <ErrorBoundary
          fallback={(reset) => (
            <ErrorFallback
              onClose={() => {
                setZoomIndex(null);
                reset();
              }}
            />
          )}
        >
          <ZoomDialog
            media={media}
            orderNumber={orderNumber}
            carouselIndex={zoomIndex}
            open
            close={() => setZoomIndex(null)}
          />
        </ErrorBoundary>
      )}
    </>
  );
};
